import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

/**
 * Shared market creation form.
 *
 * Props:
 *   - categories: list of {id, name} fetched by the caller.
 *   - onCreated(market): callback after a successful POST /markets.
 *   - forCreator (bool): when true, hides admin-only privileges (is_featured / is_live)
 *     and shows a creator-friendly intro. The backend also strips these flags for
 *     non-admin callers, but hiding them in the UI keeps the form clean.
 */
export default function CreateMarketForm({ categories, onCreated, forCreator = false }) {
    const [form, setForm] = useState({
        title: "",
        description: "",
        category_id: categories[0]?.id || "",
        options: ["Sí", "No"],
        tags: "",
        resolution_source: "",
        is_featured: false,
        is_live: false,
        image_url: "",
        closes_in_days: 7,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!form.category_id && categories[0]) {
            setForm((f) => ({ ...f, category_id: categories[0].id }));
        }
    }, [categories, form.category_id]);

    const setOpt = (i, v) => {
        const opts = [...form.options];
        opts[i] = v;
        setForm({ ...form, options: opts });
    };

    const submit = async () => {
        if (!form.title || !form.category_id) {
            toast.error("Completa título y categoría");
            return;
        }
        const cleanOpts = form.options.map((o) => o.trim()).filter(Boolean);
        if (cleanOpts.length < 2) {
            toast.error("Mínimo 2 opciones");
            return;
        }
        setSubmitting(true);
        const closes = new Date(Date.now() + Number(form.closes_in_days) * 24 * 60 * 60 * 1000).toISOString();
        try {
            const { data } = await api.post("/markets", {
                title: form.title,
                description: form.description,
                category_id: form.category_id,
                type: cleanOpts.length === 2 ? "binary" : "multiple",
                options: cleanOpts,
                closes_at: closes,
                resolution_source: form.resolution_source,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                is_featured: form.is_featured,
                is_live: form.is_live,
                image_url: form.image_url || null,
            });
            toast.success("Mercado creado");
            onCreated?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-3">
            {forCreator && (
                <div className="rounded-lg bg-p2w-purple/5 border border-p2w-purple/30 p-3 text-xs text-p2w-gray">
                    Publicas el mercado bajo tu handle. Gana comisión sobre el pozo cuando se resuelva.
                    Asegúrate de declarar una <b className="text-white">fuente de resolución</b> verificable.
                </div>
            )}
            <Field label="Pregunta del mercado" testid="market-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <div>
                <Label className="text-xs uppercase tracking-widest text-p2w-gray">Descripción</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="market-desc-input" className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Categoría</Label>
                    <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                        <SelectTrigger className="mt-2 bg-p2w-elevated border-p2w-line text-white" data-testid="market-cat-select">
                            <SelectValue placeholder="Categoría" />
                        </SelectTrigger>
                        <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Cierra en (días)</Label>
                    <Input type="number" min={1} max={365} value={form.closes_in_days} onChange={(e) => setForm({ ...form, closes_in_days: e.target.value })} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
                </div>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-widest text-p2w-gray">Opciones</Label>
                <div className="mt-2 space-y-2">
                    {form.options.map((opt, i) => (
                        <div key={i} className="flex gap-2">
                            <Input value={opt} onChange={(e) => setOpt(i, e.target.value)} data-testid={`market-option-${i}`} className="bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
                            {form.options.length > 2 && (
                                <button onClick={() => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) })} className="p-2 rounded-lg text-p2w-red hover:bg-p2w-red/10">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => setForm({ ...form, options: [...form.options, ""] })} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white text-xs h-8">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Añadir opción
                    </Button>
                </div>
            </div>

            <Field label="Fuente de resolución" testid="market-source-input" value={form.resolution_source} onChange={(v) => setForm({ ...form, resolution_source: v })} />
            <Field label="Tags (separados por coma)" testid="market-tags-input" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
            <Field label="URL imagen (opcional)" testid="market-img-input" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />

            {!forCreator && (
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="accent-p2w-green" />
                        <span className="text-white">Destacado</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.is_live} onChange={(e) => setForm({ ...form, is_live: e.target.checked })} className="accent-p2w-green" />
                        <span className="text-white">En vivo</span>
                    </label>
                </div>
            )}

            <DialogFooter className="mt-3">
                <Button onClick={submit} disabled={submitting} data-testid="confirm-create-market" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold disabled:opacity-60">
                    {submitting ? "Creando…" : "Crear mercado"}
                </Button>
            </DialogFooter>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">{label}</Label>
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
        </div>
    );
}
